import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { useLang, SECTORS } from "@/i18n";

export default function SearchBar({ initial = {}, variant = "light", live = false, onFilter }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const [type, setType] = useState(initial.type || "");
  const [guests, setGuests] = useState(initial.guests || "");
  const [pool, setPool] = useState(initial.pool || "");
  const [secteur, setSecteur] = useState(initial.secteur || "");

  const current = { type, guests, pool, secteur };
  const update = (k, v) => {
    const next = { ...current, [k]: v };
    if (k === "type") setType(v);
    if (k === "guests") setGuests(v);
    if (k === "pool") setPool(v);
    if (k === "secteur") setSecteur(v);
    if (live && onFilter) onFilter(next);
  };

  const submit = (e) => {
    e.preventDefault();
    if (live && onFilter) { onFilter(current); return; }
    const params = new URLSearchParams();
    if (type) params.set("type", type);
    if (guests) params.set("guests", guests);
    if (pool) params.set("pool", pool);
    if (secteur) params.set("secteur", secteur);
    navigate(`/locations?${params.toString()}`);
  };

  const hero = variant === "hero";
  const field = hero
    ? "h-12 w-full rounded-md border border-white/50 bg-white/10 px-4 text-sm text-white placeholder-white/60 outline-none backdrop-blur-md transition-colors duration-200 focus:border-white [&>option]:text-maquis-deep"
    : "field-input h-12";
  const labelCls = hero
    ? "mb-2 block text-[11px] uppercase tracking-[0.2em] text-white/85"
    : "field-label";

  return (
    <form
      onSubmit={submit}
      data-testid="location-search-bar"
      className={
        hero
          ? "grid grid-cols-1 gap-4 rounded-xl border border-white/40 bg-white/10 p-5 shadow-[0_24px_70px_-30px_rgba(28,45,55,0.7)] backdrop-blur-xl sm:grid-cols-2 lg:grid-cols-5 lg:items-end"
          : live
            ? "mx-auto grid w-full max-w-4xl grid-cols-1 gap-5 rounded-md border border-maquis-deep/10 bg-casse/90 p-4 shadow-[0_20px_60px_-30px_rgba(42,51,35,0.35)] backdrop-blur-md sm:grid-cols-4 sm:items-end"
            : "grid grid-cols-1 gap-4 rounded-md border border-maquis-deep/10 bg-casse/90 p-4 shadow-[0_20px_60px_-30px_rgba(42,51,35,0.35)] backdrop-blur-md sm:grid-cols-2 sm:p-6 lg:grid-cols-5 lg:items-end"
      }
    >
      <div>
        <label className={labelCls} htmlFor="search-type">{t.search.type}</label>
        <select
          id="search-type"
          data-testid="search-type-select"
          value={type}
          onChange={(e) => update("type", e.target.value)}
          className={field}
        >
          <option value="">{t.search.all_types}</option>
          {Object.entries(t.types).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>
      <div>
        <label className={labelCls} htmlFor="search-secteur">{t.search.sector}</label>
        <select
          id="search-secteur"
          data-testid="search-secteur-select"
          value={secteur}
          onChange={(e) => update("secteur", e.target.value)}
          className={field}
        >
          <option value="">{t.search.sector_all}</option>
          {SECTORS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>
      <div>
        <label className={labelCls} htmlFor="search-guests">{t.search.guests}</label>
        <input
          id="search-guests"
          data-testid="search-guests-input"
          type="number" min="1" max="30"
          value={guests}
          onChange={(e) => update("guests", e.target.value)}
          placeholder={t.contact.guests_placeholder}
          className={field}
        />
      </div>
      <div>
        <label className={labelCls} htmlFor="search-pool">{t.search.pool}</label>
        <select
          id="search-pool"
          data-testid="search-pool-select"
          value={pool}
          onChange={(e) => update("pool", e.target.value)}
          className={field}
        >
          <option value="">{t.search.pool_any}</option>
          <option value="oui">{t.search.pool_yes}</option>
          <option value="non">{t.search.pool_no}</option>
        </select>
      </div>
      {!live && (
      <div>
        <button
          type="submit"
          data-testid="search-submit-button"
          className={
            hero
              ? "flex h-12 w-full items-center justify-center gap-2 rounded-md border border-white/60 bg-white/15 text-xs uppercase tracking-[0.2em] text-white backdrop-blur-md transition-colors duration-300 hover:border-casse hover:bg-casse hover:text-maquis-deep"
              : "btn-outline-elegant w-full text-maquis-deep hover:bg-maquis-deep hover:text-casse"
          }
        >
          {hero && <Search size={15} strokeWidth={1.5} />}
          {t.search.button}
        </button>
      </div>
      )}
    </form>
  );
}
